import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Badge,
  Text,
  VStack,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Icon,
  Tooltip,
  Avatar,
  AvatarBadge
} from '@chakra-ui/react';
import { 
  FiUser, 
  FiMail, 
  FiClock, 
  FiCheckCircle,
  FiAlertCircle,
  FiTrendingUp
} from 'react-icons/fi';

const TeamPerformance = ({ teamData, onRefresh }) => {
  const formatTime = (ms) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  const getPerformanceColor = (responseRate) => {
    if (responseRate >= 80) return 'green';
    if (responseRate >= 60) return 'yellow';
    if (responseRate >= 40) return 'orange';
    return 'red';
  };

  const getPerformanceBadge = (responseRate) => {
    if (responseRate >= 80) return { text: 'Excelente', color: 'green' };
    if (responseRate >= 60) return { text: 'Bom', color: 'blue' };
    if (responseRate >= 40) return { text: 'Regular', color: 'yellow' };
    return { text: 'Precisa Melhorar', color: 'red' };
  };

  // Calculate team averages
  const teamStats = React.useMemo(() => {
    if (!teamData || teamData.length === 0) {
      return {
        totalEmails: 0,
        avgResponseRate: 0,
        avgResponseTime: 0,
        totalPending: 0
      };
    }

    const total = teamData.reduce((acc, member) => ({
      emails: acc.emails + member.total,
      replied: acc.replied + member.replied,
      pending: acc.pending + member.pending,
      responseTime: acc.responseTime + (member.avgResponseTime || 0)
    }), { emails: 0, replied: 0, pending: 0, responseTime: 0 });

    return {
      totalEmails: total.emails,
      avgResponseRate: total.emails > 0 ? (total.replied / total.emails * 100).toFixed(1) : 0,
      avgResponseTime: teamData.length > 0 ? total.responseTime / teamData.length : 0,
      totalPending: total.pending
    };
  }, [teamData]);

  // Sort team data by total emails
  const sortedTeamData = React.useMemo(() => {
    return [...teamData].sort((a, b) => b.total - a.total);
  }, [teamData]);

  // Get top performer
  const topPerformer = React.useMemo(() => {
    if (!teamData || teamData.length === 0) return null;
    return [...teamData].sort((a, b) => b.responseRate - a.responseRate)[0];
  }, [teamData]);

  return (
    <VStack spacing={6} align="stretch">
      {/* Team Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="blue.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiMail} mr={2} />
            Total de Emails
          </StatLabel>
          <StatNumber>{teamStats.totalEmails}</StatNumber>
          <StatHelpText>Distribuídos entre {teamData.length} membros</StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="green.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiCheckCircle} mr={2} />
            Taxa de Resposta Média
          </StatLabel>
          <StatNumber>{teamStats.avgResponseRate}%</StatNumber>
          <StatHelpText>
            <Badge colorScheme={getPerformanceColor(teamStats.avgResponseRate)}>
              {getPerformanceBadge(teamStats.avgResponseRate).text}
            </Badge>
          </StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="purple.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiClock} mr={2} />
            Tempo Médio de Resposta
          </StatLabel>
          <StatNumber>{formatTime(teamStats.avgResponseTime)}</StatNumber>
          <StatHelpText>Entre todos os membros</StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="orange.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiAlertCircle} mr={2} />
            Emails Pendentes
          </StatLabel>
          <StatNumber>{teamStats.totalPending}</StatNumber>
          <StatHelpText>Aguardando resposta</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Top Performer Highlight */}
      {topPerformer && (
        <Box
          bg="gradient-to-r"
          bgGradient="linear(to-r, blue.50, purple.50)"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="gold"
        >
          <HStack spacing={4}>
            <Avatar name={topPerformer.name} size="md">
              <AvatarBadge boxSize="1.25em" bg="gold" borderColor="white">
                <Icon as={FiTrendingUp} color="white" boxSize="0.7em" />
              </AvatarBadge>
            </Avatar>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold">Melhor Desempenho</Text>
              <Text>{topPerformer.name}</Text>
            </VStack>
            <Box flex={1} />
            <VStack align="end" spacing={0}>
              <Text fontWeight="bold" color="green.600">
                {topPerformer.responseRate.toFixed(1)}% Taxa de Resposta
              </Text>
              <Text fontSize="sm" color="gray.600">
                {topPerformer.replied} de {topPerformer.total} emails respondidos
              </Text>
            </VStack>
          </HStack>
        </Box>
      )}

      {/* Team Performance Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Box bg="gray.50" px={4} py={2}>
          <Text fontWeight="bold">Desempenho Individual</Text>
        </Box>
        
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Membro</Th>
              <Th isNumeric>Total</Th>
              <Th isNumeric>Respondidos</Th>
              <Th isNumeric>Pendentes</Th>
              <Th isNumeric>Ignorados</Th>
              <Th>Taxa de Resposta</Th>
              <Th>Tempo Médio</Th>
              <Th>Desempenho</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sortedTeamData.map((member) => {
              const performance = getPerformanceBadge(member.responseRate);
              return (
                <Tr key={member.userId} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <HStack>
                      <Avatar name={member.name} size="sm" />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{member.name}</Text>
                        <Text fontSize="xs" color="gray.500">{member.email}</Text>
                      </VStack>
                    </HStack>
                  </Td>
                  <Td isNumeric fontWeight="medium">{member.total}</Td>
                  <Td isNumeric color="green.600">{member.replied}</Td>
                  <Td isNumeric color="orange.600">{member.pending}</Td>
                  <Td isNumeric color="gray.500">{member.ignored}</Td>
                  <Td>
                    <VStack align="stretch" spacing={1}>
                      <HStack>
                        <Text fontSize="sm" fontWeight="medium">
                          {member.responseRate.toFixed(1)}%
                        </Text>
                      </HStack>
                      <Progress 
                        value={member.responseRate} 
                        size="xs" 
                        colorScheme={getPerformanceColor(member.responseRate)}
                        borderRadius="full"
                      />
                    </VStack>
                  </Td>
                  <Td>
                    <Tooltip label={`${(member.avgResponseTime / 1000 / 60).toFixed(0)} minutos`}>
                      <Text>{formatTime(member.avgResponseTime)}</Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Badge colorScheme={performance.color}>
                      {performance.text}
                    </Badge>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        
        {teamData.length === 0 && (
          <Box p={8}>
            <Text textAlign="center" color="gray.500">
              Nenhum dado de desempenho disponível
            </Text>
          </Box>
        )}
      </Box>
    </VStack>
  );
};

export default TeamPerformance;